import React, { useRef, useEffect } from 'react';
import { Animated, Easing, Platform, StyleSheet, View } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';

// Theme-based gradient system that only renders when explicitly enabled
export const ThemeGradientOverlay = ({ 
  opacity = 0, 
  borderRadius = 0, 
  direction = 'horizontal',
  enabled = false 
}) => {
  // Only render if explicitly enabled AND opacity > 0
  if (!enabled || !opacity || opacity === 0) return null;
  
  return (
    <Animated.View 
      pointerEvents="none" 
      style={[
        StyleSheet.absoluteFillObject, 
        { 
          opacity, 
          borderRadius, 
          overflow: 'hidden' 
        }
      ]}
    >
      <LinearGradient
        colors={['rgba(255,255,255,0.0)', 'rgba(255,255,255,0.16)']}
        start={direction === 'horizontal' ? { x: 0, y: 0.5 } : { x: 0.5, y: 0 }}
        end={direction === 'horizontal' ? { x: 1, y: 0.5 } : { x: 0.5, y: 1 }}
        style={StyleSheet.absoluteFill}
      />
    </Animated.View>
  );
};

// Full-screen background used on every page: pure black fill + shimmering blue glow at bottom
export const ScreenBackground = () => {
  const shimmerAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    Animated.loop(
      Animated.sequence([
        Animated.timing(shimmerAnim, {
          toValue: 1,
          duration: 2800,
          easing: Easing.inOut(Easing.sin),
          useNativeDriver: true,
        }),
        Animated.timing(shimmerAnim, {
          toValue: 0,
          duration: 2800,
          easing: Easing.inOut(Easing.sin),
          useNativeDriver: true,
        }),
      ])
    ).start();
  }, []);

  const shimmerOpacity = shimmerAnim.interpolate({ inputRange: [0, 1], outputRange: [0.4, 1.0] });

  return (
    <>
      <View pointerEvents="none" style={[StyleSheet.absoluteFill, { backgroundColor: '#000000' }]} />
      <Animated.View pointerEvents="none" style={[screenBgStyles.glow, { opacity: shimmerOpacity }]}>
        {Platform.OS === 'web' ? (
          <View style={[StyleSheet.absoluteFill, {
            background: 'radial-gradient(ellipse 80% 60% at 50% 100%, rgba(30,80,220,0.18) 0%, transparent 70%)'
          }]} />
        ) : (
          <LinearGradient
            colors={['transparent', 'rgba(30,80,220,0.14)']}
            style={StyleSheet.absoluteFill}
            start={{ x: 0.5, y: 0 }}
            end={{ x: 0.5, y: 1 }}
          />
        )}
      </Animated.View>
    </>
  );
};

const screenBgStyles = StyleSheet.create({
  glow: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '50%',
    pointerEvents: 'none',
  },
});

export const ThemeGlassBackground = ({ 
  opacity = 0, 
  borderRadius = 0,
  enabled = false 
}) => {
  // Only render if explicitly enabled AND opacity > 0
  if (!enabled || !opacity || opacity === 0) return null;
  
  return (
    <Animated.View 
      pointerEvents="none" 
      style={[
        StyleSheet.absoluteFillObject, 
        { 
          opacity, 
          borderRadius, 
          overflow: 'hidden' 
        }
      ]}
    >
      <LinearGradient
        colors={['rgba(0,0,0,0.0)', 'rgba(116,132,154,0.15)']}
        start={{ x: 0.5, y: 0 }}
        end={{ x: 0.5, y: 1 }}
        style={StyleSheet.absoluteFill}
      />
    </Animated.View>
  );
};
